import React, { ChangeEvent } from 'react';
import {
	Button,
	Checkbox,
	FormControlLabel,
	Grid,
	Typography,
} from '@mui/material';
import { Category as CategoryType } from '@Types';
import { useGlobalization } from '@Translations/useGlobalization';

// The unmodified pre-redesign title area — untouched behaviour, kept in its
// own component so the flag-off render path stays easy to diff against main
// and doesn't add to the (flag-aware) Category component's own complexity.
interface ClassicCategoryHeaderProps {
	category?: CategoryType;
	forLoF: boolean;
	isLoading: boolean;
	canBeDiscarded: boolean;
	vlClassName: string;
	onUseExclusivelyChange: (event: ChangeEvent<HTMLInputElement>) => void;
	onDiscardClick: () => void;
	onAddComponent?: (
		forLoF: boolean,
		category: CategoryType | undefined,
	) => void;
}

export const ClassicCategoryHeader = ({
	category,
	forLoF,
	isLoading,
	canBeDiscarded,
	vlClassName,
	onUseExclusivelyChange,
	onDiscardClick,
	onAddComponent,
}: ClassicCategoryHeaderProps) => {
	const { t } = useGlobalization();

	let placeholderName: string;
	if (forLoF) {
		placeholderName = t('Category');
	} else {
		placeholderName = t('Components');
	}

	return (
		<Grid container className={isLoading ? 'wait' : ''}>
			<div className={vlClassName}></div>
			<Grid xs={6} item container>
				{category?.isCondition ? (
					<>
						<Grid item xs={12}>
							<Typography
								variant="h6"
								style={{ marginTop: '-5px' }}>
								{category.name}
							</Typography>
						</Grid>
						<Grid item xs={12} style={{ display: 'flex' }}>
							<FormControlLabel
								style={{ marginLeft: '0px', marginTop: '-7px' }}
								control={
									<Checkbox
										style={{ padding: '0px' }}
										sx={{
											'& .MuiSvgIcon-root': {
												fontSize: 14,
											},
										}}
										checked={category?.useThisOnly}
										onChange={onUseExclusivelyChange}
										inputProps={{
											'aria-label': 'secondary checkbox',
										}}
									/>
								}
								label={
									<div
										title={t(
											'When selected you are choosing to override all general LoF components when condition is available for the asset.',
										)}>
										<Typography
											variant="body2"
											style={{ marginLeft: '6px' }}>
											{t(`If condition data is
										present, use it
										exclusively for LoF`)}
										</Typography>
									</div>
								}
							/>
						</Grid>
					</>
				) : (
					<Grid item xs={12}>
						<Typography variant="h6">
							{category ? category.name : placeholderName}
						</Typography>
					</Grid>
				)}
			</Grid>
			<Grid
				xs={6}
				item
				container
				justifyContent="flex-end"
				style={{ paddingTop: '2px' }}>
				{!canBeDiscarded ||
				isLoading ||
				category?.isCondition ? null : (
					<Button
						variant="outlined"
						color="primary"
						onClick={onDiscardClick}
						style={{ marginRight: '8px' }}>
						{t('Discard')}
					</Button>
				)}
				{isLoading ? null : (
					<Button
						variant="outlined"
						color="primary"
						onClick={() => onAddComponent?.(forLoF, category)}>
						{t('Add Component')}
					</Button>
				)}
			</Grid>
		</Grid>
	);
};
